import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../components/ui/Button.js';
import { ToastStack } from '../../components/ui/Toast.js';
import { useApi } from '../../hooks/useApi.js';
import { useToasts } from '../../hooks/useToasts.js';
import { ApiError } from '../../lib/api.js';
import type { DiaFestivoLista, ListaConFechas } from './types.js';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

// Las fechas llegan de la API como ISO ancladas en UTC (ver comentario de
// convención en dias-festivos.service.ts) — se extraen los componentes
// directamente del string, sin pasar por Date/toLocaleDateString (que
// aplicarían la zona horaria del navegador y podrían correr el día).
function fmtFecha(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

// Prompt AC-2 — Pantalla 1: tabla plana simple (Nombre/Desde/Hasta/Total),
// mismo patrón que la referencia visual de Frappe HRMS ("Holiday List
// Name"/"Total Holidays" en la lista). El detalle (secciones de agregar
// fines de semana/festivos) vive solo en DiasFestivosDetalle.tsx.
export function DiasFestivosPage() {
  const { get, patch } = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toasts, showToast, dismiss } = useToasts();

  const { data: listas, isLoading } = useQuery({
    queryKey: ['dias-festivos-listas'],
    queryFn: () => get<DiaFestivoLista[]>('/dias-festivos-listas'),
  });

  // Prompt BB-1 — reemplaza el ícono de basura por un switch bidireccional
  // (mismo patrón que UsuariosPage.tsx): soft-delete ya existía a nivel de
  // servicio, esto solo le da control visual — y ahora también permite
  // reactivar, algo que el DELETE (un solo sentido) nunca soportó.
  const toggleMutation = useMutation({
    mutationFn: ({ id, activa }: { id: string; activa: boolean }) => patch(`/dias-festivos-listas/${id}/activa`, { activa }),
    onSuccess: (_data, { activa }) => {
      queryClient.invalidateQueries({ queryKey: ['dias-festivos-listas'] });
      showToast('success', activa ? 'Holiday List reactivada' : 'Holiday List desactivada');
    },
    onError: err => showToast('error', errorMessage(err, 'No fue posible actualizar el estado de la lista')),
  });

  // GET /dias-festivos-listas no trae el conteo de fechas — se pide el
  // detalle de cada lista para el total (mismo patrón useQueries ya usado
  // en el resto del sistema, ej. cascade de catálogo por área en tickets).
  const detalles = useQueries({
    queries: (listas ?? []).map(l => ({
      queryKey: ['dias-festivos-listas', l.id],
      queryFn: () => get<ListaConFechas>(`/dias-festivos-listas/${l.id}`),
    })),
  });
  const totalPorLista = new Map((listas ?? []).map((l, i) => [l.id, detalles[i]?.data?.fechas.length]));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)' }}>Días Festivos</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>Holiday Lists — fines de semana y festivos oficiales por año</div>
        </div>
        <Button onClick={() => navigate('/dias-festivos/nuevo')}>+ Nueva Lista</Button>
      </div>

      <div className="table-wrap">
        <div className="table-scroll">
          <table className="main-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Desde</th>
                <th>Hasta</th>
                <th>Total de días festivos</th>
                <th style={{ textAlign: 'center' }}>Activa</th>
              </tr>
            </thead>
            <tbody>
              {!listas || listas.length === 0 ? (
                <tr>
                  <td className="empty-td" colSpan={5}>
                    {isLoading ? 'Cargando…' : 'No hay Holiday Lists todavía'}
                  </td>
                </tr>
              ) : (
                listas.map(l => (
                  <tr key={l.id}>
                    <td>
                      <span className="tk-folio" onClick={() => navigate(`/dias-festivos/${l.id}`)}>
                        {l.nombre}
                      </span>
                    </td>
                    <td style={{ color: 'var(--muted)' }}>{fmtFecha(l.fecha_inicio)}</td>
                    <td style={{ color: 'var(--muted)' }}>{fmtFecha(l.fecha_fin)}</td>
                    <td>{totalPorLista.get(l.id) ?? '—'}</td>
                    <td onClick={e => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'center' }}>
                      <label className="tog" title={l.activa ? 'Desactivar Holiday List' : 'Reactivar Holiday List'}>
                        <input
                          type="checkbox"
                          checked={l.activa}
                          onChange={e => toggleMutation.mutate({ id: l.id, activa: e.target.checked })}
                        />
                        <span className="tog-slider" />
                      </label>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
